import { db } from '$db/local/db';
import type { Node } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, notDeleted } from './common';

export async function listNodes(userId = getActiveUserId()): Promise<Node[]> {
  const rows = await db.nodes.where('userId').equals(userId).toArray();
  return rows
    .filter(notDeleted)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)); // Forzamos el orden de inserción
}

export async function getNode(id: string): Promise<Node | undefined> {
  return db.nodes.get(id);
}

export async function createNode(
  parentId: string | null,
  title: string,
  type: string,
  templateId: string | null = null
): Promise<Node> {
  const userId = getActiveUserId();
  const now = nowIso();
  
  const siblings = await db.nodes.where('userId').equals(userId).filter(n => n.parentId === parentId).toArray();
  // Calculamos el orden basándonos en el valor máximo actual, no en la cantidad.
  // Esto evita que al borrar nodos o importar en masa, los nuevos nodos compartan el mismo "order".
  const maxOrder = siblings.reduce((max, n) => Math.max(max, n.order ?? 0), -1);
  const order = maxOrder + 1;

  const node: Node = {
    id: newId(),
    userId,
    parentId,
    title,
    type,
    order,
    completed: false,
    templateId,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };

  await db.nodes.add(node);
  return node;
}

export async function updateNode(id: string, patch: Partial<Node>): Promise<void> {
  await db.nodes.update(id, { ...patch, updatedAt: nowIso() });
}

export async function deleteNodeCascade(id: string): Promise<void> {
  const now = nowIso();
  const children = await db.nodes.where('parentId').equals(id).toArray();
  for (const child of children) {
    if (!child.deletedAt) {
      await deleteNodeCascade(child.id);
    }
  }
  await db.nodes.update(id, { deletedAt: now, updatedAt: now });
}

export async function toggleNodeCompleted(id: string, completed: boolean): Promise<void> {
  await db.nodes.update(id, { completed, updatedAt: nowIso() });
}

export async function toggleNodeCascadeCompleted(id: string, completed: boolean): Promise<void> {
  const now = nowIso();
  const children = await db.nodes.where('parentId').equals(id).toArray();
  for (const child of children) {
    await toggleNodeCascadeCompleted(child.id, completed);
  }
  await db.nodes.update(id, { completed, updatedAt: now });
}

export async function listDeletedRootNodes(userId = getActiveUserId()): Promise<Node[]> {
  const rows = await db.nodes.where('userId').equals(userId).toArray();
  return rows.filter(n => n.deletedAt !== null && n.parentId === null);
}

export async function listAllDeletedNodes(userId = getActiveUserId()): Promise<Node[]> {
  const rows = await db.nodes.where('userId').equals(userId).toArray();
  return rows.filter(n => n.deletedAt !== null);
}

export async function restoreNodeCascade(id: string): Promise<void> {
  const now = nowIso();
  const children = await db.nodes.where('parentId').equals(id).toArray();
  for (const child of children) {
    await restoreNodeCascade(child.id);
  }
  await db.nodes.update(id, { deletedAt: null, updatedAt: now });
}

export async function hardDeleteNodeCascade(id: string): Promise<void> {
  const children = await db.nodes.where('parentId').equals(id).toArray();
  for (const child of children) {
    await hardDeleteNodeCascade(child.id);
  }
  await db.nodes.delete(id);
}

// Crea un path de ancestros para el breadcrumb
export async function getNodePath(id: string): Promise<Node[]> {
  const path: Node[] = [];
  let currentId: string | null = id;
  while (currentId) {
    const node: Node | undefined = await db.nodes.get(currentId);
    if (!node || node.deletedAt) break;
    path.unshift(node);
    currentId = node.parentId;
  }
  return path;
}